import { Map } from "immutable";
import StyleUtils from "./StyleUtils";
import { DEFAULT_BUTTON_VALUES } from "./Element/ButtonPropertyMaps";
import { gatherProps, getProp, inheritProp } from "./Merge/MergeUtils.mjs";
import AppConstants from "@/constants/AppConstants";
const { BORDER_CIRCLE_RAD } = AppConstants;

const GATHERABLE = ["shadow.style"];

export const formatBorderRadius = (radius) => {
  if (radius === undefined || radius === null) return undefined;
  return radius === BORDER_CIRCLE_RAD ? radius : String(radius).split(" ").map((val) => `${val}px`).join(" ");
};

/**
 * Resolves button styles from theme data into CSS objects
 * @param {Object} params
 * @param {import('immutable').Map} params.buttonStyles - Button styles from theme
 * @param {import('immutable').Map} params.theme - Full theme object
 * @param {string} params.layout - Layout type ('desktop' or 'mobile')
 * @param {import('immutable').Map} params.font - Font object
 * @returns {Object} Object containing { regularCSS, hoverCSS }
 */
export const resolveButtonStyles = ({ buttonStyles, theme, layout, font, inherit = true }) => {
  const getPropValue = (prop, isHover = false) => {
    if (isHover && layout === "desktop") {
      const hoverProp = `hover.${prop}`;
      const baseProp = prop;

      return (
        getProp(buttonStyles, "desktop", hoverProp) ??
        inheritProp(buttonStyles, "desktop", baseProp, theme) ??
        inheritProp(buttonStyles, "desktop", hoverProp, theme) ??
        DEFAULT_BUTTON_VALUES[baseProp]
      );
    }

    if (GATHERABLE.includes(prop)) {
      return gatherProps(buttonStyles, layout, [prop], true, inherit);
    }

    return (
      getProp(buttonStyles, layout, prop) ??
      (inherit ? inheritProp(buttonStyles, layout, prop, theme) : undefined) ??
      DEFAULT_BUTTON_VALUES[prop]
    );
  };

  const getSafeColorValue = (colorProp) => {
    if (!colorProp) return null;
    const colorValue = StyleUtils.getColorValue(colorProp, theme);
    return colorValue || null;
  };

  const getPaddingValue = (padding) => {
    if (!padding) return undefined;
    return typeof padding === "string" ? padding : `${padding}px`;
  };

  const getLetterSpacing = (lS) => {
    if (lS === undefined || lS === null) return undefined;
    return `${lS}em`;
  };

  const getTextTransform = (lC) => {
    if (!lC || lC === "none") return "none";
    return lC;
  };

  const baseCSS = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "auto",
    maxWidth: "100%",
    minHeight: "fit-content",
    cursor: "pointer",
    textAlign: "center",
    boxSizing: "border-box",
  };

  const regularCSS = {
    ...baseCSS,
  };

  const bgcolor = getPropValue("bgcolor");
  if (bgcolor && getSafeColorValue(bgcolor)) {
    regularCSS.backgroundColor = getSafeColorValue(bgcolor);
  }

  const borderWidth = getPropValue("bW");
  const borderColor = getPropValue("bC");
  if (borderWidth && borderColor && getSafeColorValue(borderColor)) {
    regularCSS.border = `${borderWidth}px solid ${getSafeColorValue(borderColor)}`;
  }

  const borderRadius = getPropValue("border.rad");
  const formattedBorderRadius = formatBorderRadius(borderRadius);
  if (formattedBorderRadius !== undefined) {
    regularCSS.borderRadius = formattedBorderRadius;
  }

  const padding = getPropValue("padding");
  if (padding) {
    regularCSS.padding = getPaddingValue(padding);
  }

  const textColor = getPropValue("text.c");
  if (textColor && getSafeColorValue(textColor)) {
    regularCSS.color = getSafeColorValue(textColor);
  }

  const textFont = getPropValue("text.font");
  if (textFont) {
    regularCSS.fontFamily = font?.get("family") || textFont;
    regularCSS.fontWeight = font?.get("weight") || 400;
    const fontStyle = font?.get("style") || "normal";
    if (fontStyle) {
      regularCSS.fontStyle = fontStyle;
    }
  }

  const textSize = getPropValue("text.size");
  if (textSize) {
    regularCSS.fontSize = `${textSize}px`;
  }

  const letterSpacing = getPropValue("text.lS");
  if (letterSpacing !== undefined) {
    regularCSS.letterSpacing = getLetterSpacing(letterSpacing);
  }

  const textTransform = getPropValue("text.lC");
  if (textTransform) {
    regularCSS.textTransform = getTextTransform(textTransform);
  }

  const lineHeight = getPropValue("text.lH");
  if (lineHeight) {
    regularCSS.lineHeight = lineHeight;
  }

  const opacity = getPropValue("o");
  if (opacity !== undefined) {
    regularCSS.opacity = opacity / 100;
  }

  const shadow = getPropValue("shadow.style");
  if (shadow) {
    const shadowCSS = StyleUtils.getShadowStyle(Map(shadow), theme, "button");
    Object.assign(regularCSS, shadowCSS);
  }

  const blur = getPropValue("blur");
  if (blur && blur > 0) {
    regularCSS.filter = `blur(${blur}px)`;
  }

  const fillType = getPropValue("fillType");
  if (fillType === "gradient") {
    const gc1 = getPropValue("gc1");
    const gc2 = getPropValue("gc2");
    const gr = getPropValue("gr") || 0;

    if (gc1 && gc2) {
      const color1 = getSafeColorValue(gc1);
      const color2 = getSafeColorValue(gc2);

      if (color1 && color2) {
        regularCSS.backgroundImage = `linear-gradient(${gr}deg, ${color2} 0%, ${color1} 100%)`;
        regularCSS.backgroundColor = "transparent";
      }
    }
  }

  const hoverCSS = { ...regularCSS };

  if (layout === "desktop") {
    const hoverBgcolor = getPropValue("bgcolor", true);
    if (hoverBgcolor && getSafeColorValue(hoverBgcolor)) {
      hoverCSS.backgroundColor = getSafeColorValue(hoverBgcolor);
    }

    const hoverBorderWidth = getPropValue("bW", true);
    const hoverBorderColor = getPropValue("bC", true);
    if (hoverBorderWidth && hoverBorderColor && getSafeColorValue(hoverBorderColor)) {
      hoverCSS.border = `${hoverBorderWidth}px solid ${getSafeColorValue(hoverBorderColor)}`;
    }

    const hoverBorderRadius = getPropValue("border.rad", true);
    const formattedHoverBorderRadius = formatBorderRadius(hoverBorderRadius); 
    if (formattedHoverBorderRadius !== undefined) {
      hoverCSS.borderRadius = formattedHoverBorderRadius;
    }

    const hoverPadding = getPropValue("padding", true);
    if (hoverPadding) {
      hoverCSS.padding = getPaddingValue(hoverPadding);
    }

    const hoverTextColor = getPropValue("text.c", true);
    if (hoverTextColor && getSafeColorValue(hoverTextColor)) {
      hoverCSS.color = getSafeColorValue(hoverTextColor);
    }

    const hoverTextFont = getPropValue("text.font", true);
    if (hoverTextFont) {
      hoverCSS.fontFamily = theme.getIn(["fonts", hoverTextFont, "family"], hoverTextFont);
      hoverCSS.fontWeight = theme.getIn(["fonts", hoverTextFont, "weight"], 400);
    }
    const fontStyle = theme.getIn(["fonts", hoverTextFont, "style"], font?.get("style"));
    if (fontStyle) {
      hoverCSS.fontStyle = fontStyle;
    }

    const hoverTextSize = getPropValue("text.size", true);
    if (hoverTextSize) {
      hoverCSS.fontSize = `${hoverTextSize}px`;
    }

    const hoverLetterSpacing = getPropValue("text.lS", true);
    if (hoverLetterSpacing !== undefined) {
      hoverCSS.letterSpacing = getLetterSpacing(hoverLetterSpacing);
    }

    const hoverTextTransform = getPropValue("text.lC", true);
    if (hoverTextTransform) {
      hoverCSS.textTransform = getTextTransform(hoverTextTransform);
    }

    const hoverLineHeight = getPropValue("text.lH", true);
    if (hoverLineHeight) {
      hoverCSS.lineHeight = hoverLineHeight;
    }

    const hoverOpacity = getPropValue("o", true);
    if (hoverOpacity !== undefined) {
      hoverCSS.opacity = hoverOpacity / 100;
    }

    const hoverBlur = getPropValue("blur", true);
    if (hoverBlur && hoverBlur > 0) {
      hoverCSS.filter = `blur(${hoverBlur}px)`;
    }

    const hoverDuration = getPropValue("dur", true);
    if (hoverDuration) {
      regularCSS.transitionDuration = `${hoverDuration}s`;
      hoverCSS.transitionDuration = `${hoverDuration}s`;
    }

    const hoverShadow = getPropValue("shadow", true);
    if (hoverShadow) {
      const hoverShadowCSS = StyleUtils.getShadowStyle(
        Map({ shadow: hoverShadow }),
        theme,
        "button",
      );
      Object.assign(hoverCSS, hoverShadowCSS);
    }

    const hoverFillType = getPropValue("fillType", true);
    if (hoverFillType === "gradient") {
      const hoverGc1 = getPropValue("gc1", true);
      const hoverGc2 = getPropValue("gc2", true);
      const hoverGr = getPropValue("gr", true) || 0;

      if (hoverGc1 && hoverGc2) {
        const hoverColor1 = getSafeColorValue(hoverGc1);
        const hoverColor2 = getSafeColorValue(hoverGc2);

        if (hoverColor1 && hoverColor2) {
          hoverCSS.backgroundImage = `linear-gradient(${hoverGr}deg, ${hoverColor2} 0%, ${hoverColor1} 100%)`;
          hoverCSS.backgroundColor = "transparent";
        }
      }
    }
  }

  return {
    regularCSS,
    hoverCSS,
  };
};

export default { resolveButtonStyles };
